import { supabase } from '@/lib/supabase'
import { getCommissionStage } from '../utils/maturity'

export type PayoutRow = {
  id: string
  affiliate_user_id: string
  amount_cents: number
  fee_cents: number
  currency: string
  status: string
  scheduled_for: string | null
  paid_at: string | null
  method: string | null
  bank_account_id: string | null
  notes: string | null
  created_at: string
}

export type PayoutReceipt = {
  url: string
  bucket: string
  path: string
  content_type?: string | null
  size_bytes?: number | null
}

export async function listAffiliatePayouts(affiliateUserId: string): Promise<PayoutRow[]> {
  const { data, error } = await supabase
    .from('affiliate_payouts')
    .select(
      'id, affiliate_user_id, amount_cents, fee_cents, currency, status, scheduled_for, paid_at, method, bank_account_id, notes, created_at',
    )
    .eq('affiliate_user_id', affiliateUserId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data as any) || []
}

export async function listAffiliatePayoutsPaged(
  affiliateUserId: string,
  page = 1,
  pageSize = 25,
): Promise<{ rows: PayoutRow[]; total: number }> {
  const from = Math.max(0, (page - 1) * pageSize)
  const to = Math.max(0, from + pageSize - 1)
  const { data, error, count } = await supabase
    .from('affiliate_payouts')
    .select(
      'id, affiliate_user_id, amount_cents, fee_cents, currency, status, scheduled_for, paid_at, method, bank_account_id, notes, created_at',
      { count: 'exact' },
    )
    .eq('affiliate_user_id', affiliateUserId)
    .order('created_at', { ascending: false })
    .range(from, to)
  if (error) throw error
  return { rows: (data as any) || [], total: Number(count || 0) }
}

function parsePayoutReceipt(notes: string | null | undefined): PayoutReceipt | null {
  if (!notes) return null
  try {
    const obj = JSON.parse(notes)
    if (!obj || obj.kind !== 'affiliate_payout_receipt') return null
    if (!obj.url || !obj.bucket || !obj.path) return null
    return {
      url: String(obj.url),
      bucket: String(obj.bucket),
      path: String(obj.path),
      content_type: obj.content_type ?? null,
      size_bytes: typeof obj.size_bytes === 'number' ? obj.size_bytes : null,
    }
  } catch {
    return null
  }
}

function serializePayoutReceipt(receipt: PayoutReceipt): string {
  return JSON.stringify({
    kind: 'affiliate_payout_receipt',
    url: receipt.url,
    bucket: receipt.bucket,
    path: receipt.path,
    content_type: receipt.content_type ?? null,
    size_bytes: typeof receipt.size_bytes === 'number' ? receipt.size_bytes : null,
  })
}

export function extractPayoutReceiptFromRow(row: PayoutRow): PayoutReceipt | null {
  return parsePayoutReceipt(row.notes)
}

export async function getPayoutReceiptsByIds(
  affiliateUserId: string,
  payoutIds: string[],
): Promise<Record<string, PayoutReceipt>> {
  const ids = Array.from(new Set((payoutIds || []).filter(Boolean)))
  if (!ids.length) return {}
  const { data, error } = await supabase
    .from('affiliate_payouts')
    .select('id, notes')
    .eq('affiliate_user_id', affiliateUserId)
    .in('id', ids)
  if (error) throw error
  const map: Record<string, PayoutReceipt> = {}
  const rows = (data as any[]) || []
  for (const row of rows) {
    const id = row?.id ? String(row.id) : null
    if (!id) continue
    const receipt = parsePayoutReceipt(row?.notes ?? null)
    if (receipt) map[id] = receipt
  }
  return map
}

export async function savePayoutReceipt(
  affiliateUserId: string,
  payoutId: string,
  receipt: PayoutReceipt | null,
): Promise<void> {
  const notes = receipt ? serializePayoutReceipt(receipt) : null
  const { error } = await supabase
    .from('affiliate_payouts')
    .update({ notes })
    .eq('affiliate_user_id', affiliateUserId)
    .eq('id', payoutId)
  if (error) throw error
}

export type EarnedCommission = { id: string; amount_cents: number; matured_at: string | null }

export async function listEarnedCommissionsForAffiliate(
  affiliateUserId: string,
  from?: string | null,
  to?: string | null,
): Promise<EarnedCommission[]> {
  let q = supabase
    .from('affiliate_commissions')
    .select('id, amount_cents, matured_at')
    .eq('affiliate_id', affiliateUserId)
    .eq('status', 'earned')
    .is('payout_id', null)
    .order('matured_at', { ascending: true })
  if (from) q = q.gte('matured_at', from)
  if (to) q = q.lte('matured_at', to)
  const { data, error } = await q
  if (error) throw error
  return (data as any) || []
}

export async function createPayoutForAffiliate(affiliateUserId: string): Promise<PayoutRow> {
  const { data, error } = await supabase.functions.invoke('admin-affiliate-create-payout', {
    body: { affiliate_user_id: affiliateUserId } as any,
  })
  if (error) {
    const msg = (error as any)?.context?.status === 403 ? 'Acao restrita a administradores (master).' : error.message
    throw new Error(msg)
  }
  return data?.payout as PayoutRow
}

export async function createPayoutForAffiliateCommissions(
  affiliateUserId: string,
  commissionIds: string[],
): Promise<PayoutRow> {
  if (!commissionIds || commissionIds.length === 0) {
    throw new Error('Nenhuma comissao disponivel para payout neste mes.')
  }
  const { data, error } = await supabase.functions.invoke('admin-affiliate-create-payout', {
    body: { affiliate_user_id: affiliateUserId, commission_ids: commissionIds } as any,
  })
  if (error) {
    const msg = (error as any)?.context?.status === 403 ? 'Acao restrita a administradores (master).' : error.message
    throw new Error(msg)
  }
  return data?.payout as PayoutRow
}

export async function markAffiliateMonthPaid(affiliateUserId: string, year: number, month: number): Promise<void> {
  const start = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0))
  const next = new Date(Date.UTC(year, month, 1, 0, 0, 0))

  const { data, error } = await supabase
    .from('v_affiliate_commissions_detailed')
    .select('commission_id, amount_cents, status, matures_at, matured_at, paid_at, payout_id')
    .eq('affiliate_user_id', affiliateUserId)
    .gte('matures_at', start.toISOString())
    .lt('matures_at', next.toISOString())
  if (error) throw error
  const rows = (data as any[]) || []

  const nowMs = Date.now()
  const maturedRows = rows.filter((r) => {
    const stage = getCommissionStage(r, nowMs)
    const hasPayout = !!r?.payout_id
    return stage === 'matured' && !hasPayout
  })

  const ids = maturedRows.map((r) => r.commission_id as string).filter(Boolean)
  if (!ids.length) {
    throw new Error('Nenhuma comissao maturada disponivel para este mes.')
  }

  const { data: created, error: createError } = await supabase.functions.invoke('admin-affiliate-create-payout', {
    body: { affiliate_user_id: affiliateUserId, commission_ids: ids } as any,
  })
  if (createError) {
    const msg =
      (createError as any)?.context?.status === 403
        ? 'Acao restrita a administradores (master).'
        : createError.message
    throw new Error(msg)
  }
  const payoutId = (created as any)?.payout?.id as string | undefined
  if (!payoutId) throw new Error('Falha ao criar payout do mes.')

  await finalizePayout(payoutId)
}

export async function finalizePayout(payoutId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('admin-affiliate-finalize-payout', {
    body: { payout_id: payoutId } as any,
  })
  if (error) {
    const msg = (error as any)?.context?.status === 403 ? 'Acao restrita a administradores (master).' : error.message
    throw new Error(msg)
  }
}

export async function cancelPayout(payoutId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('admin-affiliate-cancel-payout', {
    body: { payout_id: payoutId } as any,
  })
  if (error) {
    const msg = (error as any)?.context?.status === 403 ? 'Acao restrita a administradores (master).' : error.message
    throw new Error(msg)
  }
}
